import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Plane, LayoutDashboard, Ticket, MessageSquare, XCircle, IndianRupee,
  TrendingUp, TrendingDown, LogOut, Settings, Search, Bell, Filter, ArrowUpRight,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { ADMIN_MOCK } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const iconMap = { Ticket, IndianRupee, MessageSquare, XCircle };

const statusStyles = {
  Confirmed: "bg-green-100 text-green-700 border-green-200",
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
  New: "bg-blue-100 text-blue-700 border-blue-200",
  Contacted: "bg-teal-100 text-teal-700 border-teal-200",
  "Quote Sent": "bg-violet-100 text-violet-700 border-violet-200",
  Booked: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setAuthed(localStorage.getItem("pmr_admin") === "1");
  }, []);

  if (authed === false && !localStorage.getItem("pmr_admin")) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("pmr_admin");
    toast({ title: "Signed out", description: "See you soon!" });
    navigate("/admin/login");
  };

  const filteredBookings = ADMIN_MOCK.recentBookings.filter((b) =>
    !query ||
    b.id.toLowerCase().includes(query.toLowerCase()) ||
    b.customer.toLowerCase().includes(query.toLowerCase()) ||
    b.item.toLowerCase().includes(query.toLowerCase())
  );

  const maxRevenue = Math.max(...ADMIN_MOCK.revenueByCategory.map((r) => r.value));
  const maxBookings = Math.max(...ADMIN_MOCK.monthlyBookings.map((r) => r.value));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-950 text-white p-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-400 flex items-center justify-center">
            <Plane className="w-5 h-5 text-white -rotate-45" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Admin</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Console</div>
          </div>
        </Link>

        <nav className="mt-8 space-y-1 flex-1">
          <a href="#dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white font-medium text-sm">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </a>
          <a href="#bookings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Ticket className="w-4 h-4" /> Bookings
          </a>
          <a href="#enquiries" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <MessageSquare className="w-4 h-4" /> Enquiries
          </a>
          <Link to="/admin/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <IndianRupee className="w-4 h-4" /> Price Management
          </Link>
          <a href="#settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Settings className="w-4 h-4" /> Settings
          </a>
        </nav>

        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-300 text-sm">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="lg:ml-64">
        {/* Topbar */}
        <div className="sticky top-0 bg-white border-b border-slate-200 z-10">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="relative max-w-md w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search bookings, customers..."
                className="pl-9 h-10"
              />
            </div>
            <div className="flex items-center gap-3">
              <button className="relative w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                <Bell className="w-4 h-4 text-slate-700" />
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-orange-500" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white font-bold flex items-center justify-center text-sm">A</div>
                <div className="hidden md:block">
                  <div className="text-sm font-semibold text-slate-900">Admin</div>
                  <div className="text-xs text-slate-500">Super Admin</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Welcome back, Admin
            </h1>
            <p className="text-slate-500 mt-1">Here's what's happening with PMR Travels today.</p>
          </div>

          {/* Stats */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ADMIN_MOCK.stats.map((s) => {
              const Icon = iconMap[s.icon];
              const TrendIcon = s.trend === "up" ? TrendingUp : TrendingDown;
              return (
                <div key={s.label} className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-lg transition">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${s.trend === "up" ? "text-green-700" : "text-red-600"}`}>
                      <TrendIcon className="w-3 h-3" /> {s.change}
                    </span>
                  </div>
                  <div className="mt-4 text-3xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {s.value}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{s.label}</div>
                </div>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">Monthly Bookings</h3>
                <Badge variant="outline" className="text-xs">Last 6 months</Badge>
              </div>
              <div className="mt-6 flex items-end gap-3 h-52">
                {ADMIN_MOCK.monthlyBookings.map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center">
                      <span className="text-xs font-bold text-slate-700 mb-1">{m.value}</span>
                      <div
                        className="w-full bg-gradient-to-t from-teal-600 to-teal-400 rounded-t-md transition-all hover:from-orange-500 hover:to-orange-400"
                        style={{ height: `${(m.value / maxBookings) * 180}px` }}
                      />
                    </div>
                    <div className="text-xs text-slate-500 font-medium">{m.month}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">Revenue by Category</h3>
                <Badge variant="outline" className="text-xs">This month</Badge>
              </div>
              <div className="mt-5 space-y-3">
                {ADMIN_MOCK.revenueByCategory.map((r) => (
                  <div key={r.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-700 font-medium">{r.label}</span>
                      <span className="text-slate-500">₹{r.value}k</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${r.color} transition-all`} style={{ width: `${(r.value / maxRevenue) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs — bookings / enquiries */}
          <Tabs defaultValue="bookings">
            <TabsList className="h-11">
              <TabsTrigger value="bookings" className="gap-2"><Ticket className="w-4 h-4" /> Recent Bookings</TabsTrigger>
              <TabsTrigger value="enquiries" className="gap-2"><MessageSquare className="w-4 h-4" /> Enquiries</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-4">
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">Recent Bookings</h3>
                    <p className="text-xs text-slate-500">{filteredBookings.length} of {ADMIN_MOCK.recentBookings.length} shown</p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-2"><Filter className="w-3.5 h-3.5" /> Filter</Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600">
                        <th className="text-left px-6 py-3 font-semibold">Booking ID</th>
                        <th className="text-left px-6 py-3 font-semibold">Customer</th>
                        <th className="text-left px-6 py-3 font-semibold">Type</th>
                        <th className="text-left px-6 py-3 font-semibold">Item</th>
                        <th className="text-right px-6 py-3 font-semibold">Amount</th>
                        <th className="text-left px-6 py-3 font-semibold">Status</th>
                        <th className="text-left px-6 py-3 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((b) => (
                        <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-6 py-4 font-mono font-semibold text-slate-900">{b.id}</td>
                          <td className="px-6 py-4 text-slate-800">{b.customer}</td>
                          <td className="px-6 py-4"><Badge variant="outline" className="text-xs">{b.type}</Badge></td>
                          <td className="px-6 py-4 text-slate-700">{b.item}</td>
                          <td className="px-6 py-4 text-right font-semibold text-slate-900">₹{b.amount.toLocaleString("en-IN")}</td>
                          <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[b.status]}`}>{b.status}</span></td>
                          <td className="px-6 py-4 text-slate-500">{b.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="enquiries" className="mt-4">
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">Recent Enquiries</h3>
                    <p className="text-xs text-slate-500">{ADMIN_MOCK.enquiries.length} enquiries</p>
                  </div>
                  <Button className="bg-teal-600 hover:bg-teal-700 h-9 gap-1">
                    Export CSV <ArrowUpRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600">
                        <th className="text-left px-6 py-3 font-semibold">ID</th>
                        <th className="text-left px-6 py-3 font-semibold">Name</th>
                        <th className="text-left px-6 py-3 font-semibold">Phone</th>
                        <th className="text-left px-6 py-3 font-semibold">Destination</th>
                        <th className="text-left px-6 py-3 font-semibold">Travellers</th>
                        <th className="text-left px-6 py-3 font-semibold">Status</th>
                        <th className="text-left px-6 py-3 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ADMIN_MOCK.enquiries.map((e) => (
                        <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-6 py-4 font-mono font-semibold text-slate-900">{e.id}</td>
                          <td className="px-6 py-4 text-slate-800">{e.name}</td>
                          <td className="px-6 py-4 text-slate-700">{e.phone}</td>
                          <td className="px-6 py-4 text-slate-700">{e.destination}</td>
                          <td className="px-6 py-4 text-slate-700">{e.travellers}</td>
                          <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[e.status]}`}>{e.status}</span></td>
                          <td className="px-6 py-4 text-slate-500">{e.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="pt-4 pb-8 text-center text-xs text-orange-600 font-semibold">
            DEMO MODE — All admin data is MOCKED (no real backend connected).
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
